import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import {
  createSetupIntent,
  fetchPaymentMethods,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from "@/api/paymentMethods";
import type { PaymentMethod } from "@/interfaces/paymentMethod";

const key = (groupId: number) => ["paymentMethods", groupId] as const;

/** How long to keep asking for the new row after a card is added, and how often. */
const ADDED_POLL_INTERVAL_MS = 1500;
const ADDED_POLL_MAX_TRIES = 6;

/** A group's saved cards. Disabled until a groupId is present. */
export function usePaymentMethods(groupId: number | null | undefined) {
  return useQuery({
    queryKey: ["paymentMethods", groupId],
    queryFn: () => fetchPaymentMethods(groupId as number),
    enabled: groupId != null,
  });
}

/**
 * Call the returned function once a card has been added. A single invalidate is not enough:
 * `setup_intent.succeeded` is the authoritative writer and that WEBHOOK can land after the
 * modal closes, so refetching immediately usually reads a list without the new row — and
 * nothing refetches again, leaving the screen convinced no card exists.
 *
 * So it invalidates now and then polls until the row appears. It stops when the count rises
 * ABOVE its pre-add value, not merely above zero: a group that already had cards would
 * satisfy "> 0" on the first tick and stop before the new row exists.
 *
 * Shared by the group billing panel and the campaign-builder checkout, so both wait the same
 * way — the lag belongs to the webhook, not to either screen.
 */
export function useAwaitAddedPaymentMethod(groupId: number | null | undefined): () => void {
  const queryClient = useQueryClient();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  }, []);

  // An unmount mid-poll would otherwise keep invalidating a query nobody is reading.
  useEffect(() => stop, [stop]);

  return useCallback(() => {
    if (groupId == null) return;
    // Kills any previous poll — two cards added back to back would otherwise race.
    stop();
    const queryKey = key(groupId);
    const baseline = queryClient.getQueryData<PaymentMethod[]>(queryKey)?.length ?? 0;
    let tries = 0;
    const invalidate = () => queryClient.invalidateQueries({ queryKey });
    invalidate();
    pollRef.current = setInterval(() => {
      tries += 1;
      invalidate();
      const current = queryClient.getQueryData<PaymentMethod[]>(queryKey);
      if ((current && current.length > baseline) || tries >= ADDED_POLL_MAX_TRIES) stop();
    }, ADDED_POLL_INTERVAL_MS);
  }, [groupId, queryClient, stop]);
}

/**
 * Mints a SetupIntent for the Add-Payment modal. Deliberately does NOT invalidate the list:
 * the card is not saved when the intent is created, only after confirmSetup succeeds — the
 * modal refetches then. (The webhook is the authoritative writer; the refetch is optimistic.)
 */
export function useCreateSetupIntent(groupId: number) {
  return useMutation({
    mutationFn: () => createSetupIntent(groupId),
  });
}

/**
 * Sets a card as default with an OPTIMISTIC update: the star/badge flips instantly, then the
 * server call persists it. On error we roll back and let the caller surface it; on settle we
 * refetch to reconcile with Stripe (the source of truth).
 */
export function useSetDefaultPaymentMethod(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: number) => setDefaultPaymentMethod(groupId, paymentMethodId),
    onMutate: async (paymentMethodId) => {
      await queryClient.cancelQueries({ queryKey: key(groupId) });
      const previous = queryClient.getQueryData<PaymentMethod[]>(key(groupId));
      queryClient.setQueryData<PaymentMethod[]>(key(groupId), (old) =>
        old?.map((c) => ({ ...c, isDefault: c.id === paymentMethodId })),
      );
      return { previous };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key(groupId), ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(groupId) }),
  });
}

/** Removes a card with an optimistic update (drops the row immediately; rolls back on error). */
export function useRemovePaymentMethod(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: number) => removePaymentMethod(groupId, paymentMethodId),
    onMutate: async (paymentMethodId) => {
      await queryClient.cancelQueries({ queryKey: key(groupId) });
      const previous = queryClient.getQueryData<PaymentMethod[]>(key(groupId));
      queryClient.setQueryData<PaymentMethod[]>(key(groupId), (old) =>
        old?.filter((c) => c.id !== paymentMethodId),
      );
      return { previous };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key(groupId), ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(groupId) }),
  });
}
